/**
 * O que não trava o documento emitido (E140.F3, ADR-0096 §2.2): os pontos que pedem o juízo do
 * perito, as limitações que a equipe registrou e o que está no papel (campo de formulário não preenchido, campo em
 * branco). Cada seção abre por expansão, para o painel não esconder a falha técnica atrás deles.
 * E155.F3: o ponto com campo oferece "Rever leitura de um campo", que abre o cartão na ficha.
 */
import { Button } from '@/components/ui';
import {
  paginaDaPendencia,
  paginaDoAchado,
  type AchadoDoDocumentoData,
  type PendenciaDaLeituraData,
} from '@/lib/conferencia_da_leitura';
import type { CampoDaFichaId, PaginaNaFonte } from '@/lib/ficha_da_leitura';
import { podeReverLeitura } from '@/lib/releitura_da_leitura';

import { ROTULO_DO_ACHADO, ondeEsta, ondeEstaOAchado, preencher, reverLeitura, t } from './textos';

type VerNaFonte = (alvo: PaginaNaFonte) => void;

/** O campo que o perito quer rever na fonte, e a peça no código declarado. */
export interface AlvoDaReleitura {
  campo: CampoDaFichaId;
  referenteId: string | null;
}
export type ReverLeitura = (alvo: AlvoDaReleitura) => void;

function BotaoDeRever({
  pendencia,
  onRever,
}: {
  pendencia: PendenciaDaLeituraData;
  onRever: ReverLeitura | undefined;
}): JSX.Element | null {
  const campo = pendencia.campo;
  if (campo === null || onRever === undefined || !podeReverLeitura(campo)) return null;
  return (
    <Button
      type="button"
      size="sm"
      variant="link"
      onClick={() => onRever({ campo, referenteId: pendencia.referente_id })}
      data-testid="conferencia-rever"
    >
      {reverLeitura}
    </Button>
  );
}

function BotaoDaFonte({ alvo, onVer }: { alvo: PaginaNaFonte | null; onVer: VerNaFonte }) {
  if (alvo === null) return null;
  return (
    <Button type="button" size="sm" variant="link" onClick={() => onVer(alvo)}>
      {t.verNaFonte}
    </Button>
  );
}

function Secao({
  titulo,
  texto,
  testId,
  children,
}: {
  titulo: string;
  texto?: string;
  testId: string;
  children: JSX.Element[];
}): JSX.Element | null {
  if (children.length === 0) return null;
  return (
    <details className="border border-border p-sc-3" data-testid={testId}>
      <summary className="cursor-pointer text-body-sm font-semibold text-fg">{titulo}</summary>
      {texto ? <p className="mt-sc-2 text-caption text-fg-muted">{texto}</p> : null}
      <ul className="mt-sc-2 space-y-sc-2">{children}</ul>
    </details>
  );
}

export interface PontosDoPeritoProps {
  perito: PendenciaDaLeituraData[];
  limitacoes: PendenciaDaLeituraData[];
  achados: AchadoDoDocumentoData[];
  onVerNaFonte: VerNaFonte;
  onReverLeitura?: ReverLeitura | undefined;
}

export function PontosDoPerito({
  perito,
  limitacoes,
  achados,
  onVerNaFonte,
  onReverLeitura,
}: PontosDoPeritoProps): JSX.Element {
  return (
    <div className="space-y-sc-3">
      <Secao
        titulo={preencher(t.peritoTitulo, { n: perito.length })}
        texto={t.peritoTexto}
        testId="conferencia-perito"
      >
        {perito.map((p) => (
          <li key={p.chave} className="text-body-sm text-fg" data-tipo={p.tipo}>
            <span className="font-semibold">{ondeEsta(p)}</span> — {t.pendencia[p.tipo]}{' '}
            <BotaoDaFonte alvo={paginaDaPendencia(p)} onVer={onVerNaFonte} />{' '}
            <BotaoDeRever pendencia={p} onRever={onReverLeitura} />
          </li>
        ))}
      </Secao>
      <Secao
        titulo={preencher(t.limitacoesTitulo, { n: limitacoes.length })}
        testId="conferencia-limitacoes"
      >
        {limitacoes.map((p) => (
          <li key={p.chave} className="text-body-sm text-fg">
            <span className="font-semibold">{ondeEsta(p)}</span> — {t.pendencia[p.tipo]}{' '}
            {preencher(t.limitacao, { texto: p.limitacao ?? '' })}
          </li>
        ))}
      </Secao>
      <Secao
        titulo={preencher(t.achadosTitulo, { n: achados.length })}
        texto={t.achadosTexto}
        testId="conferencia-achados"
      >
        {achados.map((a) => (
          <li key={`${a.tipo}:${a.documento_id}:${a.pagina}:${a.marca}`} className="text-body-sm">
            <span className="font-semibold">{ROTULO_DO_ACHADO[a.tipo]}</span>:{' '}
            {preencher(t.achadoMarca, { marca: a.marca, onde: ondeEstaOAchado(a) })}{' '}
            <BotaoDaFonte alvo={paginaDoAchado(a)} onVer={onVerNaFonte} />
          </li>
        ))}
      </Secao>
    </div>
  );
}
